import { useState, useRef, useEffect } from 'react';
import {
  Avatar,
  AvatarBadge,
  Badge,
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
  useColorModeValue
} from '@chakra-ui/react';
import axiosInstance from '../../../utils/axiosInstance';
import axios from 'axios';

function Profile({ userData, isEditable }) {
  const [userProfile, setUserProfile] = useState(userData?.avatar || null); // Local state for profile image
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(userData.avatar); // Local state for cover URL
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null); // Local state for the image preview
  const { isOpen, onOpen, onClose } = useDisclosure(); // For handling error modals
  const profileImage = useRef(null);
  const [isUploading, setIsUploading] = useState(false); // Flag to track upload state

  const backendUrl = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_BACKEND_URL
    : 'http://localhost:8080';

  // Open file input dialog
  const openChooseImage = () => {
    if (isEditable) {
      profileImage.current.click();
    }
  };

  // Function to change the profile image preview locally
  const changeProfilePreview = (event) => {
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
    const selected = event.target.files[0];

    if (selected && ALLOWED_TYPES.includes(selected.type)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result); // Update the preview locally
        setCurrentAvatarUrl(reader.result); // Show the new image immediately
      };
      reader.readAsDataURL(selected);
    } else {
      onOpen(); // Trigger error modal if file type is unsupported
    }
  };

  // Uploads the selected profile image to the cloud
  const handleProfileImageUpload = async (event) => {
    event.preventDefault();
    
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      try {
        setIsUploading(true); // Set the upload flag to true during the upload process

        const fileType = selectedFile.type;
        const fileName = selectedFile.name;

        // Step 1: Get signed URL from backend
        const response = await axiosInstance.put(
          `${backendUrl}/user/update-avatar/${userData.id}/${fileName}?file-type=${fileType}`
        );

        const uploadUrl = response.data.uploadUrl;
        const newAvatarUrl = response.data.avatarUrl;

        if (!uploadUrl) {
          console.error('Missing upload URL or blob URL');
          onOpen(); // Trigger error modal if URLs are missing
          setIsUploading(false); // Reset the upload flag
          return;
        }

        // Step 2: Upload image to blob storage using signed URL
        console.log(selectedFile)
        await axios.put(uploadUrl, selectedFile, {
          headers: {
            'Content-Type': fileType,
          },
        });

        // Step 3: Update avatar with the server-provided URL
        if (newAvatarUrl) {
          //setCurrentAvatarUrl(newAvatarUrl); // Switch to the uploaded image URL from the server
          console.log("Avatar image updated successfully");
        } else {
          console.error('Invalid avatar image URL');
          onOpen(); // Handle error if the response is invalid
        }
      } catch (error) {
        console.error('Failed to upload profile image:', error);
        onOpen(); // Trigger error modal on failure
      } finally {
        setIsUploading(false); // Reset the upload flag after completion
      }
    }
  };
  // Fetch the avatar from the cloud (e.g., GCP or Blob Storage)
  {/* 
  const fetchAvatar = async (fileName) => {
    // Check if fileName is empty or undefined
    if (!fileName) {
      console.log('No fileName provided, skipping fetch request');
      return; // Skip the request
    }
    const user_id = userData.id

    try {
      const { data } = await axios.get(`${backendUrl}/get-avatar/${user_id}/${fileName}`);
      if (data.url) {
        setCurrentAvatarUrl(data.url); // Update the current avatar URL
      }
    } catch (error) {
      console.error('Failed to fetch avatar:', error);
    }
  };


  // Load the avatar image on component mount
  useEffect(() => {
    fetchAvatar(currentAvatarUrl);
  }, [userData.avatar]);
*/}
  const bgColor = useColorModeValue('white', 'black');
  const avatarBorderColor = useColorModeValue('black', 'white');
  const avatarColor = useColorModeValue('black', 'white');

  return (
    <VStack spacing={3} py={5} borderBottomWidth={1} borderColor="brand.light">
      <Avatar
        size="2xl"
        name={`${userData?.first_name} ${userData?.last_name}`}
        cursor={isEditable ? 'pointer' : 'default'}
        onClick={openChooseImage}
        src={isUploading ? imagePreviewUrl : currentAvatarUrl} // Show preview during upload, then final URL
        
      >
        {isEditable && (
          <AvatarBadge bg={bgColor} boxSize="1em" borderColor={avatarBorderColor} borderWidth={7}>
            <svg width="0.4em" fill={avatarColor} viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
              />
            </svg>
          </AvatarBadge>
        )}
      </Avatar>
      <input
        hidden
        type="file"
        ref={profileImage}
        onChange={(e) => {
          if (isEditable) {
            changeProfilePreview(e); // Handle the local preview
            handleProfileImageUpload(e); // Handle the file upload to the server
          }
        }}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Something went wrong</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>File not supported!</Text>
            <HStack mt={1}>
              <Text color="brand.cadet" fontSize="sm">
                Supported types:
              </Text>
              <Badge colorScheme="green">PNG</Badge>
              <Badge colorScheme="green">JPG</Badge>
              <Badge colorScheme="green">JPEG</Badge>
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <VStack spacing={1}>
        <Heading as="h3" fontSize="xl" color="brand.dark">
          {userData?.first_name} {userData?.last_name}
        </Heading>
        <Text color="brand.gray" fontSize="sm">
          {userData?.photography_style || 'Position not specified'}
        </Text>
      </VStack>
    </VStack>
  );
}

export default Profile;
